import { FullScreenVideo } from "@components/fullScreenVideo";
import { Modal } from "@components/shared";
import ExperienceCard from "@components/spotprofile/experience-card";
import ExperiencesFilters, {
  type ExperiencesFilterOptions,
} from "@components/spotprofile/experiences-filters";
import SpotProfileLayout from "@layouts/SpotProfileLayout";
import Tab from "@components/spotprofile/tab";
import {
  getSpotCategories,
  getSpotInfo,
  getSpotTopExperiences,
} from "@lib/api";
import type {
  GetStaticPathsContext,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from "next";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "@hooks/useTranslation";
import { events, track } from "@shared/tracking";

import type { Locale } from "../../../../../i18n-config";
import { capitalize, pickByLocale } from "@lib/utils";
import { useCurrencyRate } from "@hooks/useCurrencyRate";
import { config } from "@shared/config";

export default function ExperiencesPage({
  cityName,
  backgroundImage,
  experiences: initialExperiences,
  categories,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { t } = useTranslation();

  const [experiences, setExperiences] = useState(initialExperiences);
  const [count, setCount] = useState(initialExperiences.length);
  const [filters, setFilters] = useState<ExperiencesFilterOptions>({});

  const router = useRouter();

  const [selectedExperienceId, setSelectedExperienceId] = useState<number>();

  useEffect(() => {
    const expId = Number(router.query.experience_id);
    const expIdx = initialExperiences.findIndex(
      (experience) => experience.id === expId
    );
    if (expIdx !== -1) {
      setSelectedExperienceId(expId);
    }
  }, [initialExperiences, router.query.experience_id]);

  const selectedExperience = experiences.find(
    ({ id }) => id === selectedExperienceId
  );

  const applyFilters = useCallback(
    (filters: ExperiencesFilterOptions) => {
      setFilters(filters);
      const query: Record<string, string> = {
        ...router.query,
        show_filters: "false",
      };

      let filteredExperiences = initialExperiences;

      if (filters.categories?.length) {
        filteredExperiences = filteredExperiences.filter((experience) =>
          filters.categories?.some(({ id }) =>
            experience.categories.includes(id)
          )
        );
        query.categories = filters.categories.map(({ id }) => id).join(",");
      } else {
        delete query.categories;
      }

      if (filters.sortOption) {
        filteredExperiences = filteredExperiences.sort((a, b) => {
          switch (filters.sortOption?.id) {
            case "price-asc":
              return (
                b.activities.sort((a, b) => a.minimumPrice - b.minimumPrice)[0]
                  .minimumPrice -
                a.activities.sort((a, b) => a.minimumPrice - b.minimumPrice)[0]
                  .minimumPrice
              );
            case "price-desc":
              return (
                a.activities.sort((a, b) => a.minimumPrice - b.minimumPrice)[0]
                  .minimumPrice -
                b.activities.sort((a, b) => a.minimumPrice - b.minimumPrice)[0]
                  .minimumPrice
              );
            case "popularity":
              return (
                b.activities.sort((a, b) => a.score - b.score)[0].score -
                a.activities.sort((a, b) => a.score - b.score)[0].score
              );
            default:
              return 0;
          }
        });

        query.sortOption = filters.sortOption.id;
      } else delete query.sortOption;

      setExperiences(filteredExperiences);

      router.replace(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { shallow: true }
      );
    },
    [initialExperiences, router]
  );

  const countFilteredExperiences = useCallback(
    (filters: ExperiencesFilterOptions) => {
      setCount(
        initialExperiences.filter((experience) => {
          if (filters.categories?.length) {
            return filters.categories.some(({ id }) =>
              experience.categories.includes(id)
            );
          }
          return true;
        }).length
      );
    },
    [initialExperiences]
  );

  const onCardClick = useCallback(
    (experienceId: number) => {
      setSelectedExperienceId(experienceId);
      track(events.CLICK_EXPERIENCE_CARD, {
        city_id: router.query.id as string,
        experience_id: experienceId,
        section: "experiences",
        ...(router.query.categories && {
          categories_ids: router.query.categories as string,
        }),
        ...(router.query.sortOption && {
          sort_by: router.query.sortOption as string,
        }),
      });
    },
    [router.query.categories, router.query.id, router.query.sortOption]
  );

  const locale = (router.locale ?? "en") as Locale;

  const title = `${cityName} | ${capitalize(
    t("spotprofile.experiences.title")
  )}`;

  const description = pickByLocale(
    {
      en: `Discover the best experiences in ${cityName} with beder. Press play, book, and enjoy the journey!`,
      es: `Descubre las mejores experiencias en ${cityName} con beder. ¡Dale al play, reserva y disfruta del viaje!`,
      fr: `Découvrez les meilleures expériences à ${cityName} avec beder. Appuyez sur play, réservez et profitez du voyage!`,
      ru: `Откройте для себя лучшие впечатления в ${cityName} с beder. Нажмите "плей", бронируйте и наслаждайтесь путешествием!`
    },
    locale
  );
  const currencyRate = useCurrencyRate();

  return (
    <SpotProfileLayout
      cityName={cityName}
      backgroundImage={backgroundImage}
      meta={{
        title,
        description,
      }}
      search={false}
    >
      <Tab
        text={`${t("spotprofile.experiences.in")} ${cityName}`}
        count={experiences.length}
        selectedFilters={filters}
        onFiltersChange={applyFilters}
      >
        {experiences.map((experience) => (
          <ExperienceCard
            key={experience.id}
            id={`experience-${experience.id}`}
            onClick={() => onCardClick(experience.id)}
            title={experience.name}
            score={
              experience.activities.reduce(
                (score, curr) => (curr.score < score ? score : curr.score),
                experience.activities?.[0]?.score!
              ) as number
            }
            price={experience.activities.reduce(
              (price, curr) =>
                isNaN(+price)
                  ? 0
                  : curr.minimumPrice > Number(price)
                  ? price
                  : curr.minimumPrice,
              experience.activities?.[0]?.minimumPrice
            )}
            imgUrl={experience.posts?.[0]?.thumbnailUrl}
            isPopular={experience?.activities?.some(
              ({ isPopular }) => isPopular
            )}
            currency={currencyRate.rate}
            symbol={currencyRate.symbol}
          />
        ))}
      </Tab>
      <ExperiencesFilters
        categories={categories}
        count={count}
        filters={filters}
        onFiltersChange={countFilteredExperiences}
        onApplyFilters={applyFilters}
        onClearFilters={() => applyFilters({})}
      />

      <Modal isOpen={!!selectedExperience}>
        {!!selectedExperience && (
          <FullScreenVideo
            handleClose={() => {
              setSelectedExperienceId(undefined);

              const { experience_id, exp_title, expand_activities, ...query } =
                router.query;
              router.push(
                {
                  pathname: router.pathname,
                  query,
                },
                undefined,
                { shallow: true }
              );

              track(events.CLOSE_EXPERIENCE_VIDEO, {
                city_id: router.query.id as string,
                experience_id: experience_id as string,
                section: "experiences",
                ...(router.query.categories && {
                  categories_ids: router.query.categories as string,
                }),
                ...(router.query.sortOption && {
                  sort_by: router.query.sortOption as string,
                }),
              });
            }}
            exprience={{
              experienceId: selectedExperience?.id,
              title: selectedExperience?.name,
              posts: selectedExperience?.posts,
              activities: selectedExperience?.activities,
              backgroundImage:
                selectedExperience?.activities?.[0].backgroundImage,
            }}
            section="experiences"
          />
        )}
      </Modal>
    </SpotProfileLayout>
  );
}
export async function getStaticProps(ctx: GetStaticPropsContext) {
  const cityId = ctx.params?.id as string;
  const locale = ctx.locale as "en" | "es" | "fr" | "ru";

  const {
    data: [[spotInfo]],
  } = await getSpotInfo(cityId);

  const {
    data: [experiences],
  } = await getSpotTopExperiences(cityId);
  const {
    data: [categories],
  } = await getSpotCategories();
  return {
    props: {
      cityName:
        spotInfo[
          locale === "es"
            ? "spanish_name"
            : locale === "fr"
            ? "french_name"
            : locale === "ru" && spotInfo.russian_name != null
            ? "russian_name"
            : "english_name"
        ],
      backgroundImage: spotInfo.spot_image,
      experiences: experiences
        .filter(
          ({ activity }) =>
            activity.filter((activity) => !!activity[`name_${locale}`]).length >
            0
        )
        .map((experience, idx) => ({
          id: experience.spot_top_experience_id,
          name: experience[`name_experience_${locale}`],
          posts: experience.webposts.map(({ post }) => ({
            postId: post.post_id,
            url: post.fileStorage_url,
            thumbnailUrl: post.thumbnail_url,
            userId: post.user_id,
            userThumbnail: post.User.thumbnail,
            username: post.User.username,
          })),
          activities: experience.activity
            .filter((activity) => !!activity[`name_${locale}`])
            .map((activity) => ({
              id: activity.activity_id,
              url: activity[`url_${locale}`],
              name: activity[`name_${locale}`],
              description: activity[`description_${locale}`],
              address: activity[`address_${locale}`],
              providerImgUrl: activity[`provider_${locale}_url`] ?? null,
              score: activity[`score_${locale}`],
              lang: activity[`activity_lang_${locale}`] ?? null,
              duration: activity[`duration_${locale}`],
              reviews: activity[`reviews_${locale}`],
              freeCancelation: activity[`free_cancelation_${locale}`] ?? null,
              originalPrice: Number(
                Number(activity[`original_price_${locale}`]).toFixed(1)
              ),
              minimumPrice: Number(
                Number(activity[`minimum_price_${locale}`]).toFixed(1)
              ),
              currency: activity.currency,
              isPopular: activity.most_popular,
              backgroundImage: activity.discover_image,
            })),
          categories: experience.wwwbs_spot_mu_top_experiences_activities
            .map(({ musement_activity: { musement_activities_categories } }) =>
              musement_activities_categories.map(
                ({ activity_categories_catalogue_id }) =>
                  activity_categories_catalogue_id
              )
            )
            .reduce((acc, curr) => [...acc, ...curr], []),
        })),
      categories: categories.map((category) => ({
        id: category.activity_categories_catalogue_id,
        name: category[`category_${locale}`],
      })),
    },
  };
}

export async function getStaticPaths(ctx: GetStaticPathsContext) {
  const locales = ctx.locales!;

  const arrayTopCities = config.TOP_CITIES.split(',');

  const paths = arrayTopCities.flatMap((cityId) => { //"100146" New York
    return locales.map((locale) => ({
      params: {
        id: cityId,
      },
      locale,
    }));
  });

  return {
    paths,
    fallback: false,
  };
}
